import React, { useMemo, useState } from "react";

import {
  FLOWCHART,
  START_NODE_ID,
  type DecisionNode,
  type Node,
  type Role,
} from "./flowchart";
import DeviceStatusTile from "./DeviceStatusTile";
import TicketPhotoUpload from "../tickets/TicketPhotoUpload";
import { deriveAutoAnswer, type AutoAnswer } from "./autoAnswer";
import { Button } from "../ui/button";
import type { Box } from "../../types/boxes";

interface TroubleshootWizardProps {
  /** Box that owns the open ticket — drives auto-answers. */
  box: Box;
  /** Pre-existing answer trail to resume from (loaded from ticket comments). */
  initialPath?: PathEntry[];
  /** Fired when the operator records an answer / advances. Caller persists
   *  this to the ticket comment stream so the trail survives a refresh. */
  onAdvance: (entry: PathEntry) => Promise<void> | void;
  /** Fired when operator reaches the End node + clicks Finish. */
  onFinish?: () => void;
  /** Attach a photo to the CURRENT step (`nodeId`), e.g. what the tech found
   *  or the fix they made. Omit to hide the photo control. */
  onAddPhoto?: (nodeId: string, image: string, caption: string) => Promise<void>;
}

export interface PathEntry {
  nodeId: string;
  /** For decision nodes — the branch label chosen. */
  answer?: string;
  /** True iff the answer came from the auto-answer adapter (not the operator). */
  auto?: boolean;
  /** Auto-answer source text shown for audit context. */
  autoSource?: string;
}

/** Headline + accent colors per role. Mirrors the mermaid flowchart palette. */
const ROLE_STYLES: Record<
  Role,
  { ring: string; bg: string; text: string; label: string }
> = {
  HQ: {
    ring: "ring-primary-300",
    bg: "bg-primary-50",
    text: "text-primary-800",
    label: "HQ — remote",
  },
  Maint: {
    ring: "ring-success-300",
    bg: "bg-success-50",
    text: "text-success-800",
    label: "Maint — on site",
  },
  Joint: {
    ring: "ring-warning-400",
    bg: "bg-warning-50",
    text: "text-warning-800",
    label: "Joint — HQ + Maint",
  },
  Neutral: {
    ring: "ring-secondary-300",
    bg: "bg-secondary-50",
    text: "text-secondary-700",
    label: "Either",
  },
};

export const TroubleshootWizard: React.FC<TroubleshootWizardProps> = ({
  box,
  initialPath,
  onAdvance,
  onFinish,
  onAddPhoto,
}) => {
  const [path, setPath] = useState<PathEntry[]>(
    initialPath && initialPath.length > 0
      ? initialPath
      : [{ nodeId: START_NODE_ID }],
  );
  const [busy, setBusy] = useState(false);

  const currentNodeId = path[path.length - 1]!.nodeId;
  const node: Node = FLOWCHART[currentNodeId]!;
  const roleStyle = ROLE_STYLES[node.role];

  const autoAnswer: AutoAnswer | null = useMemo(() => {
    if (node.kind !== "decision" || !node.autoAnswerKey) return null;
    return deriveAutoAnswer(node.autoAnswerKey, box);
  }, [box, node]);

  const advance = async (
    answer: string | undefined,
    auto: boolean,
    autoSource: string | undefined,
    next: string,
  ): Promise<void> => {
    setBusy(true);
    try {
      // Mark the current node as answered.
      const updatedCurrent: PathEntry = {
        ...path[path.length - 1]!,
        answer,
        auto,
        autoSource,
      };
      // Push the new current node.
      const newEntry: PathEntry = { nodeId: next };
      const newPath = [...path.slice(0, -1), updatedCurrent, newEntry];
      setPath(newPath);
      await onAdvance(updatedCurrent);
    } finally {
      setBusy(false);
    }
  };

  const back = (): void => {
    if (path.length <= 1) return;
    setPath((p) => p.slice(0, -1));
  };

  const reset = (): void => {
    setPath([{ nodeId: START_NODE_ID }]);
  };

  return (
    <div className="space-y-3">
      {/* Step 0: what is actually alive at this box. Shown before any question,
          because it is the first thing a tech checks on arriving at a ticket and
          it often settles the early nodes outright (a dead smartplug with a live
          Peplink is a very different visit from the reverse). Open on the first
          step, collapsed thereafter so an operator mid-walkthrough isn't
          re-reading it on every step. */}
      <details open={path.length === 1} className="group">
        <summary className="cursor-pointer text-xs font-semibold text-secondary-700 select-none py-1">
          Device status
          <span className="ml-1 font-normal text-secondary-500 group-open:hidden">
            — tap to show
          </span>
        </summary>
        <div className="mt-2">
          <DeviceStatusTile box={box} />
        </div>
      </details>

      {/* Breadcrumb of completed steps — earlier steps clickable to revisit. */}
      {path.length > 1 && (
        <div className="flex flex-wrap gap-1 text-[10px] text-secondary-500">
          {path.slice(0, -1).map((entry, idx) => {
            const n = FLOWCHART[entry.nodeId];
            return (
              <span
                key={idx}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-secondary-100"
                title={entry.answer ?? ""}
              >
                <span className={ROLE_STYLES[n?.role ?? "Neutral"].text}>
                  {idx + 1}.
                </span>
                <span>{n?.title.slice(0, 32) ?? entry.nodeId}</span>
                {entry.answer && (
                  <span className="font-semibold">→ {entry.answer}</span>
                )}
                {entry.auto && (
                  <span title="answered automatically from MTTR data">⚙</span>
                )}
              </span>
            );
          })}
        </div>
      )}

      {/* Current step card */}
      <div
        className={`rounded-lg border ${roleStyle.bg} ring-1 ${roleStyle.ring} p-4`}
      >
        <div className="flex items-start gap-3">
          {/* Placeholder illustration — colored block by role. Replace
              with real artwork later by setting `image` on the node. */}
          <div
            className={`shrink-0 w-14 h-14 rounded-lg flex items-center justify-center text-xl font-bold ${roleStyle.text} bg-white ring-1 ${roleStyle.ring}`}
            aria-hidden="true"
          >
            {node.image ? (
              <img src={node.image} alt="" className="w-full h-full object-cover rounded-lg" />
            ) : (
              <span>{path.length}</span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className={`text-[10px] uppercase font-semibold tracking-wide ${roleStyle.text}`}>
              {roleStyle.label}
            </div>
            <h3 className="text-base font-semibold text-secondary-900 mt-0.5">
              {node.title}
            </h3>
            <p className="text-sm text-secondary-700 mt-1">{node.body}</p>
          </div>
        </div>

        {/* Auto-answer hint */}
        {autoAnswer && (
          <div className="mt-3 text-xs px-3 py-2 rounded bg-white ring-1 ring-secondary-200">
            <div className="font-semibold text-secondary-800">
              ⚙ MTTR's read: <em>{autoAnswer.suggestedLabel}</em>
            </div>
            <div className="text-secondary-600 mt-0.5">
              {autoAnswer.source}
            </div>
          </div>
        )}

        {/* Answer buttons */}
        <div className="mt-4 flex flex-col gap-2 md:flex-row md:flex-wrap">
          {node.kind === "decision" &&
            node.branches.map((b) => {
              const isAuto = autoAnswer?.suggestedLabel === b.label;
              return (
                <Button
                  key={b.label}
                  type="button"
                  size="sm"
                  variant={isAuto ? "default" : "outline"}
                  disabled={busy}
                  onClick={() =>
                    void advance(
                      b.label,
                      isAuto,
                      isAuto ? autoAnswer?.source : undefined,
                      b.next,
                    )
                  }
                  className="min-h-[44px] flex-1 md:flex-initial md:min-h-0"
                >
                  {b.label}
                  {isAuto && (
                    <span className="ml-1.5 text-[10px] opacity-80">
                      (MTTR ⚙)
                    </span>
                  )}
                </Button>
              );
            })}
          {node.kind === "action" && (
            <Button
              type="button"
              size="sm"
              variant="default"
              disabled={busy}
              onClick={() => void advance("done", false, undefined, node.next)}
              className="min-h-[44px] flex-1 md:flex-initial md:min-h-0"
            >
              Mark done — continue
            </Button>
          )}
          {node.kind === "terminal" && (
            <>
              <Button
                type="button"
                size="sm"
                variant="default"
                onClick={() => onFinish?.()}
                className="min-h-[44px] flex-1 md:flex-initial md:min-h-0"
              >
                Finish troubleshooting
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={reset}
                className="min-h-[44px] md:min-h-0"
              >
                Restart
              </Button>
            </>
          )}
        </div>

        {/* Photo of this step -- documents what was found / what was fixed.
            Filed against the step's node id so the activity stream shows
            which question the photo answers. */}
        {onAddPhoto && (
          <div className="mt-3 pt-3 border-t border-secondary-200/70">
            <TicketPhotoUpload
              label="Add a photo of this step"
              onUpload={(image, caption) => onAddPhoto(currentNodeId, image, caption)}
            />
          </div>
        )}
      </div>

      {/* Footer controls */}
      <div className="flex justify-between text-xs">
        <button
          type="button"
          onClick={back}
          disabled={path.length <= 1}
          className="text-primary-600 underline disabled:opacity-30 disabled:no-underline"
        >
          ← Back
        </button>
        {path.length > 1 && (
          <button
            type="button"
            onClick={reset}
            className="text-secondary-500 hover:text-secondary-800 underline"
          >
            Restart troubleshooting
          </button>
        )}
      </div>
    </div>
  );
};

export default TroubleshootWizard;

// Note: re-exports the type so callers (TicketActivity) don't have to know
// about the path-entry shape coming from internal modules.
export type { Node as TroubleshootNode } from "./flowchart";
